import { useEffect, useState } from "react";
import { History, CalendarClock, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { adminFetchCampaigns, adminFetchAutoSend, adminSaveAutoSend, formatApiError } from "@/lib/auth";

const WEEKDAYS = ["Segunda-feira", "Terça-feira", "Quarta-feira", "Quinta-feira", "Sexta-feira", "Sábado", "Domingo"];

export const AdminCampaigns = () => {
    const [campaigns, setCampaigns] = useState([]);
    const [auto, setAuto] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        adminFetchCampaigns().then(setCampaigns).catch(() => {});
        adminFetchAutoSend().then(setAuto).catch(() => {});
    }, []);

    const saveAuto = async (updated) => {
        setSaving(true);
        try {
            await adminSaveAutoSend(updated);
            setAuto(updated);
            toast.success(updated.enabled
                ? `Agendamento ativo: toda ${WEEKDAYS[updated.weekday].toLowerCase()} às ${updated.hour}h.`
                : "Agendamento desativado.");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    const selectCls = "bg-ink border border-white/10 focus:border-gold/60 outline-none px-3 py-2 text-sm transition-colors";

    return (
        <div className="mt-14">
            <h2 className="font-serif text-2xl tracking-tight mb-6 flex items-center gap-3">
                <History size={18} className="text-gold" /> Campanhas & agendamento
            </h2>

            {auto && (
                <div data-testid="auto-send-panel" className="border border-white/10 bg-paper p-6 mb-8">
                    <div className="flex flex-wrap items-center gap-4">
                        <CalendarClock size={18} className="text-gold shrink-0" />
                        <div className="flex-1 min-w-[220px]">
                            <p className="font-medium text-sm">Envio automático da newsletter</p>
                            <p className="text-xs text-slate-500 mt-1">
                                Monta a edição do artigo mais recente e envia sozinho. Se você já enviou manualmente no dia, o automático não duplica.
                            </p>
                        </div>
                        <label className="flex items-center gap-2.5 cursor-pointer select-none">
                            <input
                                type="checkbox"
                                data-testid="auto-send-toggle"
                                checked={auto.enabled}
                                onChange={(e) => saveAuto({ ...auto, enabled: e.target.checked })}
                                disabled={saving}
                                className="w-4 h-4 accent-[#EAB308]"
                            />
                            <span className="text-sm text-slate-300">{auto.enabled ? "Ativo" : "Desativado"}</span>
                        </label>
                        <select
                            data-testid="auto-send-weekday"
                            value={auto.weekday}
                            onChange={(e) => saveAuto({ ...auto, weekday: Number(e.target.value) })}
                            disabled={saving || !auto.enabled}
                            className={selectCls}
                        >
                            {WEEKDAYS.map((d, i) => <option key={i} value={i}>{d}</option>)}
                        </select>
                        <select
                            data-testid="auto-send-hour"
                            value={auto.hour}
                            onChange={(e) => saveAuto({ ...auto, hour: Number(e.target.value) })}
                            disabled={saving || !auto.enabled}
                            className={selectCls}
                        >
                            {[6, 7, 8, 9, 10, 12, 18, 20].map((h) => <option key={h} value={h}>{h}h00</option>)}
                        </select>
                        {saving && <Loader2 size={15} className="animate-spin text-gold" />}
                    </div>
                    {auto.enabled && (
                        <p className="mt-4 text-xs text-gold/80 border border-gold/30 bg-gold/[0.05] px-4 py-2.5">
                            Próximo envio: {WEEKDAYS[auto.weekday].toLowerCase()} às {auto.hour}h (horário de Brasília), para todos os inscritos ativos.
                        </p>
                    )}
                </div>
            )}

            {campaigns.length === 0 ? (
                <p className="text-sm text-slate-500 border border-dashed border-white/15 px-5 py-8 text-center">Nenhuma campanha enviada ainda.</p>
            ) : (
                <div className="border border-white/10">
                    <div className="hidden md:grid grid-cols-12 gap-4 px-5 py-3 border-b border-white/10 text-[10px] uppercase tracking-[0.2em] text-slate-500">
                        <span className="col-span-6">Assunto</span>
                        <span className="col-span-2">Enviada em</span>
                        <span className="col-span-2">Entregues</span>
                        <span className="col-span-2 text-right">Origem</span>
                    </div>
                    {campaigns.map((c) => (
                        <div key={c.id} data-testid={`campaign-row-${c.id}`} className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 px-5 py-4 border-b border-white/5 items-center">
                            <span className="col-span-6 font-serif text-base leading-snug">{c.subject}</span>
                            <span className="col-span-2 text-xs text-slate-500">{new Date(c.sent_at).toLocaleString("pt-BR")}</span>
                            <span className="col-span-2 text-xs">
                                <span className="text-gold">{c.sent}</span>
                                <span className="text-slate-500">/{c.recipients}</span>
                                {c.failed > 0 && <span className="text-red-400 ml-1">({c.failed} falhas)</span>}
                            </span>
                            <span className="col-span-2 text-right text-[11px] uppercase tracking-wider text-slate-500">
                                {c.sent_by === "agendamento-automatico" ? "Automático" : "Manual"}
                            </span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};
